import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, DatePicker } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePW9318Query } from './hooks/usePW9318Query';
import type { AttendancePunchRow } from '../../services/pw9318Api';
import './PW9318Form.css';

const { RangePicker } = DatePicker;

const STATUS_LABEL: Record<string, string> = {
  S: '正常打卡',
  T: '加班打卡',
};

const columns: ColumnsType<AttendancePunchRow> = [
  { title: '部門代號', dataIndex: 'F9191_12', key: 'F9191_12', width: 90 },
  { title: '部門名稱', dataIndex: 'dept_name', key: 'dept_name', width: 110, ellipsis: true },
  { title: '人員代號', dataIndex: 'F9191_13', key: 'F9191_13', width: 90 },
  { title: '員工姓名', dataIndex: 'F901_11', key: 'F901_11', width: 80 },
  { title: '出勤日期', dataIndex: 'F9191_02', key: 'F9191_02', width: 100 },
  { title: '刷卡序號', dataIndex: 'F9191_03', key: 'F9191_03', width: 90 },
  {
    title: '狀態碼',
    dataIndex: 'F9191_11',
    key: 'F9191_11',
    width: 90,
    render: (val: string) => STATUS_LABEL[val] ?? val,
  },
  { title: '記錄主鍵', dataIndex: 'F9191_01', key: 'F9191_01', width: 110, ellipsis: true },
];

const PW9318Form: React.FC = () => {
  const { loading, data, queried, noData, runQuery, reset } = usePW9318Query();

  const [deptFrom, setDeptFrom] = useState('');
  const [deptTo, setDeptTo] = useState('');
  const [empFrom, setEmpFrom] = useState('');
  const [empTo, setEmpTo] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([
    null,
    dayjs(),
  ]);

  const handleQuery = () => {
    runQuery({
      deptFrom: deptFrom.trim() || undefined,
      deptTo: deptTo.trim() || undefined,
      empFrom: empFrom.trim() || undefined,
      empTo: empTo.trim() || undefined,
      dateFrom: dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
      dateTo: dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
    });
  };

  const handleReset = () => {
    setDeptFrom('');
    setDeptTo('');
    setEmpFrom('');
    setEmpTo('');
    setDateRange([null, dayjs()]);
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    const rows = data
      .map(
        r => `<tr>
        <td>${r.F9191_12}</td>
        <td>${r.dept_name}</td>
        <td>${r.F9191_13}</td>
        <td>${r.F901_11}</td>
        <td>${r.F9191_02}</td>
        <td>${r.F9191_03}</td>
        <td>${STATUS_LABEL[r.F9191_11] ?? r.F9191_11}</td>
      </tr>`
      )
      .join('');
    win.document.write(`<!DOCTYPE html><html><head><title>感應卡-每筆出勤明細</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}
h2{text-align:center}table{width:100%;border-collapse:collapse}
th,td{border:1px solid #333;padding:2px 5px}th{background:#f0f0f0;text-align:center}
@media print{body{margin:8mm}}</style></head><body>
<h2>感應卡-每筆出勤明細</h2>
<table><thead><tr>
<th>部門代號</th><th>部門名稱</th><th>人員代號</th><th>員工姓名</th>
<th>出勤日期</th><th>刷卡序號</th><th>狀態</th>
</tr></thead><tbody>${rows}</tbody></table>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const rowKey = (r: AttendancePunchRow) => `${r.F9191_01}_${r.F9191_03}`;

  return (
    <PermissionGuard module={Modules.PW9318} action="query">
      <div className="pw9318-container">
        <div className="pw9318-header">
          <h2>感應卡-每筆出勤明細</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>
              列印
            </Button>
          )}
        </div>

        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap size="middle">
            <div className="pw9318-filter-item">
              <label className="pw9318-label">部門代號（從）</label>
              <Input
                value={deptFrom}
                onChange={e => setDeptFrom(e.target.value)}
                placeholder="起始部門"
                style={{ width: 110 }}
                allowClear
              />
            </div>
            <div className="pw9318-filter-item">
              <label className="pw9318-label">部門代號（至）</label>
              <Input
                value={deptTo}
                onChange={e => setDeptTo(e.target.value)}
                placeholder="結束部門"
                style={{ width: 110 }}
                allowClear
              />
            </div>
            <div className="pw9318-filter-item">
              <label className="pw9318-label">人員代號（從）</label>
              <Input
                value={empFrom}
                onChange={e => setEmpFrom(e.target.value)}
                placeholder="起始人員"
                style={{ width: 120 }}
                allowClear
              />
            </div>
            <div className="pw9318-filter-item">
              <label className="pw9318-label">人員代號（至）</label>
              <Input
                value={empTo}
                onChange={e => setEmpTo(e.target.value)}
                placeholder="結束人員"
                style={{ width: 120 }}
                allowClear
              />
            </div>
            <div className="pw9318-filter-item">
              <label className="pw9318-label">出勤日期範圍</label>
              <RangePicker
                value={dateRange}
                onChange={vals =>
                  setDateRange(vals ? [vals[0], vals[1]] : [null, dayjs()])
                }
                format="YYYY-MM-DD"
                style={{ width: 240 }}
                allowEmpty={[true, false]}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleQuery}
                  loading={loading}
                >
                  查詢
                </Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>
                  清除
                </Button>
              </Space>
            </div>
          </Space>
        </Card>

        {queried && noData && (
          <Empty description="查無此資料" style={{ marginTop: 40 }} />
        )}
        {queried && !noData && (
          <Table<AttendancePunchRow>
            columns={columns}
            dataSource={data}
            rowKey={rowKey}
            loading={loading}
            size="small"
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              showTotal: t => `共 ${t} 筆`,
            }}
            scroll={{ x: 780 }}
          />
        )}
      </div>
    </PermissionGuard>
  );
};

export default PW9318Form;
